import { createFileRoute } from "@tanstack/react-router";
import { lazyNamed } from "@/lib/lazy-route";
import {
  solarPvLp,
  SOLAR_HERO_VARIANT_KEYS,
  type SolarHeroVariant,
} from "@/content/landing/solar-pv";
import { fetchProperties } from "@/lib/portfolio";
import { pageSeo, serviceJsonLdScript } from "@/lib/seo";
import { SITE } from "@/lib/site";
import { withServiceLcpPreload } from "@/lib/service-lcp-preload";
import { SERVICE_PAGE_OG_IMAGES } from "@/lib/service-page-og-images";
import { breadcrumbJsonLdScript } from "@/lib/breadcrumbs";

const LpSolarPvPage = lazyNamed(
  () => import("@/components/landing/lp-solar-pv"),
  "LpSolarPv",
);

const PATH = "/solar-pv";
const TITLE = "Solar PV for Your Home South Wales | MCS Certified | CRS Energy";
const DESCRIPTION =
  "MCS certified, DNO compliant solar PV design and install across South Wales. Optional battery storage, SEG export, manufacturer-backed warranties. Free solar estimate.";

type SolarSearch = { hero?: SolarHeroVariant };

export const Route = createFileRoute("/solar-pv")({
  validateSearch: (search: Record<string, unknown>): SolarSearch => {
    const raw = search.hero;
    if (typeof raw === "string" && (SOLAR_HERO_VARIANT_KEYS as readonly string[]).includes(raw)) {
      return { hero: raw as SolarHeroVariant };
    }
    return {};
  },
  loader: () => fetchProperties(),
  head: () =>
    withServiceLcpPreload(PATH, {
    ...pageSeo({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      image: SERVICE_PAGE_OG_IMAGES[PATH],
      ogType: "article",
    }),
    scripts: [
      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=AW-18165581626",
      },
      {
        children: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'AW-18165581626');`,
      },
      serviceJsonLdScript({
        name: "Solar PV",
        description: DESCRIPTION,
        serviceType: "Solar PV",
        url: `${SITE.url}${PATH}`,
        image: SERVICE_PAGE_OG_IMAGES[PATH],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: solarPvLp.faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }),
      },
      breadcrumbJsonLdScript([
        { label: "Home", path: "/" },
        { label: "Solar PV", path: PATH },
      ]),
    ],
  }),
  component: LpSolarPvPage,
});
